import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

export type BccOfferInput = {
  offerId?: string;
  targetFirmId: string;
  offerType: "demo" | "subscription" | "renewal" | "custom";
  discountPercent?: number;
  demoDurationDays?: number;
  initialCreditGrant?: number;
  monthlyIncludedCredits?: number;
  creditCap?: number;
  isFoundingOffer?: boolean;
  foundingReason?: string;
};

export type BccOfferListItem = {
  offerId: string;
  tenantId: string;
  offerType: string;
  status: string;
  finalMonthlyPriceAed: number;
  grossMarginPercent: number;
  initialCreditGrant: number;
  monthlyIncludedCredits: number;
  demoDurationDays: number;
  isFoundingOffer?: boolean;
  foundingReason?: string | null;
  updatedAt: string | null;
};

export type BccDashboardData = {
  cards: {
    activeTenants: number;
    demoTenants: number;
    pendingProposals: number;
    paymentFailed: number;
    openSupportIssues: number;
    lowCreditTenants: number;
  };
  finance: {
    monthlySubscriptionRevenueAed: number;
    creditRevenueAed: number;
    estimatedAiCostAed: number;
    netMarginAed: number;
    netMarginPercent: number;
  };
  health: {
    reconciliationAlerts: number;
    webhookLagging: boolean;
  };
};

export type BccTenant360Data = {
  overview: {
    tenantId: string;
    firmName: string;
    lifecycleState: string;
    status: string;
    subscriptionStatus: string;
    offerType: string;
    finalMonthlyPriceAed: number;
    discountPercent: number;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    trialExpiresAt: string | null;
  };
  aiCredits: {
    creditBalance: number;
    monthlyIncludedCredits: number;
    creditCap: number;
    lowCredit: boolean;
  };
  offers: Array<{
    offerId: string;
    offerType: string;
    status: string;
    finalMonthlyPriceAed: number;
    grossMarginPercent: number;
    updatedAt: string | null;
  }>;
  support: Array<{
    ticketId: string;
    title: string;
    status: string;
    priority: string;
    updatedAt: string | null;
  }>;
  audit: Array<{
    eventId: string;
    eventType: string;
    actorUid: string;
    reason: string;
    createdAt: string | null;
  }>;
};

export type BccAuditEvent = {
  eventId: string;
  targetId: string;
  targetType: string;
  eventType: string;
  actorUid: string;
  actorEmail: string;
  reason: string;
  createdAt: string | null;
};

export type BccTenantCommunication = {
  id: string;
  type: string;
  templateKey: string;
  recipientEmail: string;
  status: string;
  attempts: number;
  provider: string;
  providerMessageId: string;
  error: string;
  createdAt: string | null;
  updatedAt: string | null;
  deliveredAt: string | null;
  lastAttemptAt: string | null;
};

export const bccGetDashboardCallable = httpsCallable<Record<string, never>, BccDashboardData>(functions, "bccGetDashboard");

export const bccRunTaskAutomationSweepCallable = httpsCallable<Record<string, never>, { success: boolean; createdTasks: number }>(functions, "bccRunTaskAutomationSweep");

export const bccRunBillingReconciliationCallable = httpsCallable<{ limit?: number }, { success: boolean; checked: number; mismatches: number; alertsCreated: number }>(functions, "bccRunBillingReconciliation");

export const bccProcessTaskEngineCallable = httpsCallable<{ dryRun?: boolean }, { success: boolean; checked: number; escalated: number; autoResolved: number; dryRun: boolean }>(functions, "bccProcessTaskEngine");

export const bccUpdateOperatorTaskStateCallable = httpsCallable<{ taskId: string; nextState: "open" | "in_progress" | "resolved" | "dismissed"; note?: string }, { success: boolean; taskId: string; state: string }>(functions, "bccUpdateOperatorTaskState");

export const bccGetTenant360Callable = httpsCallable<{ targetFirmId: string }, BccTenant360Data>(functions, "bccGetTenant360");

export const bccListOffersCallable = httpsCallable<{ targetFirmId?: string; limit?: number }, { offers: BccOfferListItem[] }>(functions, "bccListOffers");

export const bccUpsertOfferDraftCallable = httpsCallable<BccOfferInput, { success: boolean; offer: Record<string, unknown> }>(functions, "bccUpsertOfferDraft");

export const bccSendDemoProposalCallable = httpsCallable<{ offerId: string; marginOverrideReason?: string }, { success: boolean; offerId: string; proposalToken: string; status: string }>(functions, "bccSendDemoProposal");

export const bccSendSubscriptionProposalCallable = httpsCallable<{ offerId: string; marginOverrideReason?: string }, { success: boolean; offerId: string; tenantId: string; status: string; lifecycleState: string; proposalToken?: string; proposalUrl?: string }>(functions, "bccSendSubscriptionProposal");

export type BccPublicProposal = {
  offerId: string;
  tenantId: string;
  firmName: string;
  status: string;
  offerType: string;
  proposalType: string;
  proposalVersion: number;
  finalMonthlyPriceAed: number;
  discountPercent: number;
  monthlyIncludedCredits: number;
  initialCreditGrant: number;
  demoDurationDays: number;
  paymentLinkAvailable: boolean;
  stripePaymentLinkUrl: string | null;
  snapshot: Record<string, unknown>;
};

export const bccGetProposalByTokenCallable = httpsCallable<
  { proposalToken: string },
  BccPublicProposal
>(functions, "bccGetProposalByToken");

export const bccAcceptProposalByTokenCallable = httpsCallable<
  { proposalToken: string; acceptedByEmail?: string; successUrlOrigin?: string },
  {
    success: boolean;
    offerId: string;
    tenantId: string;
    offerType: string;
    lifecycleState: string;
    demoEndAt?: string | null;
    initialCreditGrant?: number;
    newBalance?: number;
    stripePaymentLinkUrl?: string | null;
    stripePaymentLinkId?: string;
  }
>(functions, "bccAcceptProposalByToken");

export const bccHandleProposalExceptionCallable = httpsCallable<
  {
    offerId: string;
    action: "resend_proposal" | "revoke_proposal" | "extend_expiry" | "correct_email" | "mark_declined" | "mark_duplicate_tenant" | "mark_failed_payment" | "close_demo_abandoned";
    reason?: string;
    correctedEmail?: string;
    extendDays?: number;
    duplicateTenantId?: string;
  },
  {
    success: boolean;
    offerId: string;
    tenantId: string;
    action: string;
    status: string;
    lifecycleState: string;
    subscriptionStatus: string;
  }
>(functions, "bccHandleProposalException");

export const bccGenerateSubscriptionPaymentLinkCallable = httpsCallable<
  { offerId: string; successUrlOrigin?: string },
  {
    success: boolean;
    offerId: string;
    tenantId: string;
    stripePaymentLinkId: string;
    stripePaymentLinkUrl: string;
    stripePriceId: string;
    stripeCouponId: string | null;
    stripeCustomerId: string;
  }
>(functions, "generateSubscriptionPaymentLink");

export const bccConfigureFoundingSlotsCallable = httpsCallable<
  { maxSlots: number; reason: string; programName?: string },
  { success: boolean; maxSlots: number; reservedSlots: number; availableSlots: number; programName: string }
>(functions, "bccConfigureFoundingSlots");

export const bccAcceptDemoProposalCallable = httpsCallable<{ offerId: string }, { success: boolean; offerId: string; tenantId: string; lifecycleState: string; demoEndAt: string; initialCreditGrant: number; newBalance: number }>(functions, "bccAcceptDemoProposal");

export const bccTransitionTenantLifecycleCallable = httpsCallable<{ targetFirmId: string; nextState: string; reason?: string; confirmationToken?: string }, { success: boolean; targetFirmId: string; lifecycleState: string; status: string; subscriptionStatus: string }>(functions, "bccTransitionTenantLifecycle");

export const bccStartPaymentFailureGraceCallable = httpsCallable<{ targetFirmId: string; graceDays?: number; reason?: string }, { success: boolean; targetFirmId: string; graceDays: number; paymentFailureGraceState: string; paymentFailureGraceEndsAt: string }>(functions, "bccStartPaymentFailureGrace");

export const bccEnforcePaymentFailureGraceCallable = httpsCallable<{ dryRun?: boolean }, { success: boolean; checked: number; suspended: number; dryRun: boolean }>(functions, "bccEnforcePaymentFailureGrace");

export const bccGrantEmergencyCreditsCallable = httpsCallable<{ targetFirmId: string; amount: number; reason?: string; confirmationToken?: string }, { success: boolean; targetFirmId: string; amount: number; newBalance: number }>(functions, "bccGrantEmergencyCredits");

export const bccCreateOperationConfirmationCallable = httpsCallable<{ operationType: string; payload: Record<string, unknown>; reason?: string; ttlMinutes?: number }, { success: boolean; confirmationToken: string; operationType: string; expiresAt: string; ttlMinutes: number }>(functions, "bccCreateOperationConfirmation");

export const bccUpsertCreditPackCatalogItemCallable = httpsCallable<
  { packCode: string; packName?: string; packPriceAed: number; targetMarginPercent?: number; avgCostPerCreditAed?: number; reason?: string },
  {
    success: boolean;
    packCode: string;
    packName: string;
    packPriceAed: number;
    targetMarginPercent: number;
    avgCostPerCreditAed: number;
    includedCredits: number;
    estimatedCostAed: number;
    estimatedMarginAed: number;
    estimatedMarginPercent: number;
  }
>(functions, "bccUpsertCreditPackCatalogItem");

export const bccListCreditPackCatalogCallable = httpsCallable<
  { limit?: number },
  {
    packs: Array<{
      packCode: string;
      packName: string;
      packPriceAed: number;
      targetMarginPercent: number;
      avgCostPerCreditAed: number;
      includedCredits: number;
      estimatedMarginPercent: number;
      active: boolean;
      updatedAt: string | null;
    }>;
  }
>(functions, "bccListCreditPackCatalog");

export const bccQueryAuditLogCallable = httpsCallable<{ targetFirmId?: string; eventType?: string; limit?: number }, { events: BccAuditEvent[] }>(functions, "bccQueryAuditLog");

export const listTenantCommunicationsCallable = httpsCallable<
  { targetFirmId: string; limit?: number },
  { communications: BccTenantCommunication[] }
>(functions, "listTenantCommunications");

export const resendTenantCommunicationCallable = httpsCallable<
  { targetFirmId: string; notificationId: string },
  { success: boolean; notificationId: string }
>(functions, "resendTenantCommunication");
